import { useContext } from "react";
import { NumberOfferContext } from "../App";
import { ICart } from "@features/cart/ICart";
import { getProspectReference } from "@utils/storage";
import { EEirSalesSimType, getResponseToState, ICartResponse } from "@mappers/getCartMapper";
import { getStateToPayload } from "@mappers/postOfferMapper";
import { useFetch } from "./useFetch";
import { addOfferRoute, getInclusiveEquipmentsApiRoute } from "@api-routes/eirSalesFacade";
import { mapInclusiveEquipmentPayload } from "@mappers/postInclusiveEquipmentsMapper";
import { sendAddOfferToCartEvent } from "@features/dataLayer/addToCart";
import { getCartItemsNumber } from "@utils/getCartItemsNumber";

export const useAddOffer = () => {
    const [addMobileOfferState, { post: postAddMobileOffer }] = useFetch();
    const [addBroadbandOfferState, { post: postAddBroadbandOffer }] = useFetch();
    const [addInclusiveEquipmentState, { post: postAddInclusiveEquipment }] = useFetch();
    const { setNumberOfferSelected } = useContext(NumberOfferContext);

    const getMostRecentOfferId = (response: ICartResponse): number =>
        response.offers.map((offer) => offer.id).sort((a, b) => b - a)[0];

    const addMobileOffer = async (catalogOfferCode: string, prospectReference?: string): Promise<ICart | undefined> => {
        const prospect = prospectReference ?? getProspectReference();

        try {
            let newCart: ICart | undefined;

            const addOfferResponse = await postAddMobileOffer<ICartResponse>(addOfferRoute(prospect), {
                body: getStateToPayload(catalogOfferCode),
            });

            if (addOfferResponse) {
                const offerId = getMostRecentOfferId(addOfferResponse);

                const inclusiveEquipmentResponse = await postAddInclusiveEquipment<ICartResponse>(
                    getInclusiveEquipmentsApiRoute(offerId),
                    {
                        body: mapInclusiveEquipmentPayload(EEirSalesSimType.PSIM),
                    }
                );

                if (inclusiveEquipmentResponse) {
                    newCart = getResponseToState(inclusiveEquipmentResponse);

                    sendAddOfferToCartEvent(newCart, offerId);

                    setNumberOfferSelected(getCartItemsNumber(newCart));

                    return newCart;
                }
            }

            return newCart;
        } catch (error) {
            return undefined;
        }
    };

    const addBroadbandOffer = async (
        catalogOfferCode: string,
        prequalId: string,
        prospectReference?: string
    ): Promise<ICart | undefined> => {
        const prospect = prospectReference ?? getProspectReference();

        try {
            const addOfferResponse = await postAddBroadbandOffer<ICartResponse>(addOfferRoute(prospect), {
                body: getStateToPayload(catalogOfferCode, prequalId),
            });

            if (addOfferResponse) {
                const offerId = getMostRecentOfferId(addOfferResponse);
                const newCart = getResponseToState(addOfferResponse);

                sendAddOfferToCartEvent(newCart, offerId);

                setNumberOfferSelected(getCartItemsNumber(newCart));

                return newCart;
            }

            return undefined;
        } catch (error) {
            return undefined;
        }
    };

    return {
        addMobileOfferState,
        addMobileOffer,
        addInclusiveEquipmentState,
        addBroadbandOfferState,
        addBroadbandOffer,
    };
};
